import React, { useState } from 'react';
import { Keyboard, View } from 'react-native';
import { useDelayedAutoFocus } from '@/hooks/useDelayedAutoFocus';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { VStack } from '@/components/gluestack/vstack';
import { Input, InputField } from '@/components/gluestack/input';
import { FormControl, FormControlError, FormControlErrorIcon, FormControlErrorText, FormControlHelper, FormControlHelperText, FormControlLabel, FormControlLabelText } from '@/components/gluestack/form-control';
import { Button, ButtonText } from '@/components/gluestack/button';
import { AlertCircleIcon } from '@/components/gluestack/icon';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useToastMessages } from '@/hooks/useToastMessages';
import { AccountService } from '@/services/domain/AccountService';
import { useAuth } from '@/components/providers/AuthProvider';

type FormValues = { password: string };

export interface IDeleteAccountSheetProps extends IContextSheetContentProps {}

export default function DeleteAccountSheet({ onClose }: IDeleteAccountSheetProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const { signOutSoft, forget, currentCommunity } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useDelayedAutoFocus();

  const schema = z.object({
    password: z.string().min(1, { message: t('settings.account.delete.required') }),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '' },
  });

  const onSubmit = async ({ password }: FormValues) => {
    Keyboard.dismiss();
    setSubmitting(true);
    try {
      await AccountService.deleteAccount(password);
      showSuccessToast(t('settings.account.delete.success'));
      if (currentCommunity) {
        try { await forget(currentCommunity, { force: true }); } catch {}
      }
      await signOutSoft();
      onClose?.();
    } catch (error) {
      showErrorToast(t('settings.account.delete.error'), error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <VStack className="flex-1 gap-4">
      <FormControl isInvalid={!!errors.password}>
        <FormControlLabel>
          <FormControlLabelText>{t('settings.account.delete.confirmLabel')}</FormControlLabelText>
        </FormControlLabel>
        <Controller
          control={control}
          name="password"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                ref={inputRef}
                placeholder={t('settings.account.delete.placeholder')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="done"
                onSubmitEditing={handleSubmit(onSubmit)}
                type={'password'}
              />
            </Input>
          )}
        />
        <FormControlHelper>
          <FormControlHelperText>{t('settings.account.delete.confirmMessage')}</FormControlHelperText>
        </FormControlHelper>
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText>{errors.password?.message}</FormControlErrorText>
        </FormControlError>
      </FormControl>

      <View className="flex-1" />

      <Button onPress={handleSubmit(onSubmit)} isDisabled={submitting} action="negative" className="mt-2 self-end">
        <ButtonText>{t('settings.account.delete.submit')}</ButtonText>
      </Button>
    </VStack>
  );
}

DeleteAccountSheet.sheetConfig = {
  largeScreenMode: 'modal',
  size: 'sm',
  height: 'sm',
} satisfies ISheetConfig;
